import {
  Beaker,
  Hammer,
  ClipboardList,
  Gavel,
  FileText,
  List,
  Network,
  Handshake,
  Hash,
  Brain,
  Search,
  BookOpen,
  GraduationCap,
  Quote,
  Scale,
  CheckCircle,
  Globe,
  FileSearch,
  BarChart,
  Lightbulb,
  Target,
  Table,
  MessageSquarePlus,
  PenTool
} from 'lucide-react';

export interface Task {
  id: string;
  name: string;
  description: string;
  task_type?: string;
  coming_soon?: boolean;
  hidden?: boolean;
  premium_only?: boolean;
  icon?: any;
  resource_interface?: {
    input?: {
      resource_id?: boolean;
      patent_number?: boolean;
      patent_app_no?: boolean;
      publication_number?: boolean;
      use_entire_description?: boolean;
      use_entire_claims?: boolean;
      source_language?: { type: string; options?: any[]; default?: string; label?: string };
      target_language?: { type: string; options?: any[]; default?: string; label?: string };
      [key: string]: any;
    };
  };
}

export const TASK_CATEGORIES = {
  ALL: 'all',
  INVENTION_RECORD: 'invention_record',
  DRAFT_PROCESSING: 'draft_processing',
  FORM_PROCESSING: 'form_processing',
  OFFICE_ACTION: 'office_action',
  TRANSLATION: 'translation',
  LEGACY: 'legacy'
} as const;

export const TASK_CATEGORY_INFO: Record<string, { name: string; icon: any; color: string }> = {
  [TASK_CATEGORIES.ALL]: {
    name: 'All Tasks',
    icon: null,
    color: 'gray'
  },
  [TASK_CATEGORIES.INVENTION_RECORD]: {
    name: 'Invention Records',
    icon: Beaker,
    color: 'blue'
  },
  [TASK_CATEGORIES.DRAFT_PROCESSING]: {
    name: 'Patent Drafting',
    icon: Hammer,
    color: 'green'
  },
  [TASK_CATEGORIES.FORM_PROCESSING]: {
    name: 'Forms & Filing',
    icon: ClipboardList,
    color: 'slate'
  },
  [TASK_CATEGORIES.OFFICE_ACTION]: {
    name: 'Office Actions',
    icon: Gavel,
    color: 'red'
  },
  [TASK_CATEGORIES.TRANSLATION]: {
    name: 'Document Processing',
    icon: FileText,
    color: 'purple'
  },
  [TASK_CATEGORIES.LEGACY]: {
    name: 'Document Analysis',
    icon: FileText,
    color: 'gray'
  }
};

export const AVAILABLE_TASKS: Task[] = [
  // Multi-Document Q&A
  {
    id: 'multi_doc_analysis',
    name: 'Multi-Doc Q&A',
    description: 'Ask a question across multiple patent documents simultaneously and get per-document answers in a single background job',
    task_type: 'multi_doc_analysis',
    icon: MessageSquarePlus,
  },

  // Legacy Document Analysis Tasks
  {
    id: 'parts_list',
    name: 'Parts List',
    description: 'Generate a list of parts or components identified in the patent document',
    task_type: 'legacy',
    icon: List,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication selection
      }
    }
  },
  {
    id: 'spec_map',
    name: 'Specification Map',
    description: 'Map the specification details to the relevant parts and features in the document',
    task_type: 'legacy',
    icon: Network,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication selection
      }
    }
  },
  {
    id: 'antecedent_basis_check',
    name: 'Check Claims Antecedent Basis',
    description: 'Verify that all claim elements have proper antecedent basis and clear referential relationships',
    task_type: 'antecedent_basis_check',
    icon: CheckCircle,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication selection
      }
    }
  },
  {
    id: 'claims_support',
    name: 'Claims Support',
    description: 'Check claims for support in the specification and ensure they align with described parts and features',
    task_type: 'legacy',
    icon: Handshake,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication selection
      }
    }
  },
  {
    id: 'claim_chart',
    name: 'Claim Chart',
    description: 'Parse claims into phrases and identify supporting disclosure for each phrase in a three-column table format',
    task_type: 'claim_chart',
    icon: Table,
    resource_interface: {
      input: {
        resource_id: true,  // Requires patent/publication selection
        include_support: true  // Optional checkbox for including support column
      }
    }
  },
  {
    id: 'claims_with_numerals',
    name: 'Add Numerals to Claims',
    description: 'Ensure that the claims include reference numerals corresponding to parts or components in the drawings',
    task_type: 'legacy',
    icon: Hash,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication selection
      }
    }
  },
  {
    id: 'custom_analysis',
    name: 'Custom Analysis',
    description: 'Run custom analysis on patent documents with user-defined prompts',
    task_type: 'legacy',
    icon: Brain,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication/upload selection
      }
    }
  },

  // Invention Record Processing
  {
    id: 'ir_metadata',
    name: 'IR Metadata Extraction',
    description: 'Extract metadata from invention records including inventors, assignees, and technical classifications',
    task_type: 'ir_metadata',
    icon: FileSearch
  },
  {
    id: 'ir_innovation',
    name: 'IR Innovation Analysis',
    description: 'Analyze innovation aspects and technological advancements in invention records',
    task_type: 'ir_innovation',
    icon: Lightbulb
  },
  {
    id: 'ir_prior_art',
    name: 'IR Prior Art Identification',
    description: 'Identify relevant prior art and existing technologies related to the invention',
    task_type: 'ir_prior_art',
    coming_soon: true,
    icon: Search
  },
  {
    id: 'prior_art_analysis',
    name: 'Prior Art Analysis',
    description: 'Run a feature-by-feature prior art analysis comparing a technology description or patent against a list of prior art references, with Word report output',
    task_type: 'prior_art_analysis',
    hidden: true,
    icon: Target,
  },
  {
    id: 'ir_tech_class',
    name: 'IR Technology Classification',
    description: 'Classify invention records into appropriate technology categories and patent classifications',
    task_type: 'ir_tech_class',
    icon: Target
  },
  {
    id: 'technical_outline',
    name: 'IR Technical Outline',
    description: 'Create a comprehensive technical outline from an invention disclosure for patent specification drafting',
    task_type: 'technical_outline',
    icon: FileText,
    resource_interface: {
      input: {
        resource_id: false  // Does not require resource selection (uses raw text or upload)
      }
    }
  },
  {
    id: 'parts_process_outline',
    name: 'IR Parts & Process Outline',
    description: 'Generate detailed parts hierarchy and process steps with technical language, relationships, and functional connections',
    task_type: 'parts_process_outline',
    icon: Network,
    resource_interface: {
      input: {
        resource_id: false  // Does not require resource selection (uses raw text or upload)
      }
    }
  },

  {
    id: 'patent_drawing',
    name: 'Generate Patent Drawing',
    description: 'Generate a USPTO-compliant black-and-white line drawing suitable for patent filing from an image, a text description, or both',
    task_type: 'patent_drawing',
    icon: PenTool,
  },

  // Draft Processing
  {
    id: 'draft_detailed_description',
    name: 'Draft Detailed Description',
    description: 'Generate comprehensive detailed description sections from user input, invention record, and context documents',
    task_type: 'draft_detailed_description',
    icon: FileText,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication/upload selection
      }
    }
  },
  {
    id: 'draft_other_embodiments',
    name: 'Draft Other Embodiments',
    description: 'Generate alternative embodiments and variations based on user input invention description',
    task_type: 'draft_other_embodiments',
    icon: Network,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication/upload selection
      }
    }
  },
  {
    id: 'draft_claims',
    name: 'Draft Claims',
    description: 'Generate patent claims from disclosure text with proper claim formatting and structure',
    task_type: 'draft_claims',
    icon: List,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication/upload selection
      }
    }
  },
  {
    id: 'draft_background',
    name: 'Draft Background',
    description: 'Generate background section for patent applications from disclosure text',
    task_type: 'draft_background',
    icon: BookOpen,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication/upload selection
      }
    }
  },
  {
    id: 'draft_summary',
    name: 'Draft Summary',
    description: 'Generate summary section for patent applications from disclosure text',
    task_type: 'draft_summary',
    icon: FileText,
    resource_interface: {
      input: {
        resource_id: true  // Requires patent/publication/upload selection
      }
    }
  },

  // Form Processing
  {
    id: 'form_ids',
    name: 'Generate IDS Form',
    description: 'Generate Information Disclosure Statement (IDS) forms from prior art references',
    task_type: 'form_ids',
    coming_soon: true,
    icon: ClipboardList
  },
  {
    id: 'form_ads',
    name: 'Generate ADS Form',
    description: 'Generate Application Data Sheet (ADS) forms from application metadata',
    task_type: 'form_ads',
    coming_soon: true,
    icon: ClipboardList
  },
  {
    id: 'form_checklist',
    name: 'Generate Filing Checklist',
    description: 'Generate comprehensive filing checklist from application information',
    task_type: 'form_checklist',
    coming_soon: true,
    icon: CheckCircle
  },
  {
    id: 'form_ids_check',
    name: 'Check IDS Compliance',
    description: 'Validate IDS forms for compliance with USPTO requirements',
    task_type: 'form_ids_check',
    coming_soon: true,
    icon: CheckCircle
  },

  // Office Action Processing
  {
    id: 'oa_extract',
    name: 'Extract Office Action Info',
    description: 'Extract key information and requirements from office action documents',
    task_type: 'oa_extract',
    icon: FileSearch
  },
  {
    id: 'oa_analyze',
    name: 'Analyze Office Action Issues',
    description: 'Analyze office action issues and provide detailed assessment of claims and rejections',
    task_type: 'oa_analyze',
    icon: BarChart
  },
  {
    id: 'oa_summary_art',
    name: 'Summarize Office Action Prior Art',
    description: 'Summarize prior art references cited in office actions with detailed analysis',
    task_type: 'oa_summary_art',
    icon: BookOpen
  },
  {
    id: 'oa_timeline',
    name: 'Build Office Action Timeline',
    description: 'Create timeline of prosecution history from office action documents',
    task_type: 'oa_timeline',
    icon: BarChart
  },
  {
    id: 'oa_file_wrapper_notes',
    name: 'Extract File Wrapper Notes',
    description: 'Extract and organize notes from file wrapper and prosecution documents',
    task_type: 'oa_file_wrapper_notes',
    hidden: true,
    icon: FileText
  },
  {
    id: 'oa_reporting_email',
    name: 'Generate OA Reporting Message',
    description: 'Generate a professional email report summarizing office action information for client communication',
    task_type: 'oa_reporting_email',
    icon: FileText
  },

  // Translation
  {
    id: 'translate_document',
    name: 'Translate Document',
    description: 'Translate entire patent documents or technical documents between languages while preserving technical accuracy',
    task_type: 'translate_document',
    premium_only: true,
    icon: Globe
  },
  {
    id: 'translate_claims',
    name: 'Translate Claims',
    description: 'Translate patent claims with precise legal terminology preservation and proper claim formatting',
    task_type: 'translate_claims',
    premium_only: true,
    hidden: true,
    icon: Globe
  },
  {
    id: 'translate_abstract',
    name: 'Translate Abstract',
    description: 'Translate patent abstracts maintaining technical accuracy and concise language suitable for patent documentation',
    task_type: 'translate_abstract',
    premium_only: true,
    hidden: true,
    icon: Globe
  },
  {
    id: 'latex_to_docx',
    name: 'LaTeX to Word Converter',
    description: 'Convert LaTeX documents (ZIP archives) to Microsoft Word format while preserving mathematical equations and formatting',
    task_type: 'latex_to_docx',
    premium_only: true,
    icon: FileText
  },
  {
    id: 'ocr_pdf',
    name: 'PDF OCR',
    description: 'Extract text from scanned PDF documents using optical character recognition (OCR). Output as text, Word (DOCX), or JSON with positional data',
    task_type: 'ocr_pdf',
    premium_only: true,
    icon: FileSearch
  },

  // Coming Soon Tasks
  {
    id: 'compare_claims',
    name: 'Compare Claims',
    description: 'Compare claims between different patent documents to identify similarities and differences',
    task_type: 'legacy',
    coming_soon: true,
    icon: Scale
  },
  {
    id: 'add_numerals_to_abstract',
    name: 'Add Numerals to Abstract',
    description: 'Add reference numerals to abstract text to correspond with drawings and detailed description',
    task_type: 'legacy',
    coming_soon: true,
    icon: Hash
  },
  {
    id: 'compare_prior_art_to_claims',
    name: 'Compare Prior Art to Claims',
    description: 'Compare identified prior art references against patent claims to assess novelty and non-obviousness',
    task_type: 'legacy',
    coming_soon: true,
    icon: Search
  },
  {
    id: 'find_specification_support',
    name: 'Find Specification Support',
    description: 'Locate and identify specification support for claim elements and technical features',
    task_type: 'legacy',
    coming_soon: true,
    icon: BookOpen
  },
  {
    id: 'assess_claim_definiteness',
    name: 'Assess Claim Definiteness',
    description: 'Evaluate claim language for definiteness issues and ambiguous terms under 35 U.S.C. § 112(b)',
    task_type: 'legacy',
    coming_soon: true,
    icon: Gavel
  },
  {
    id: 'analyze_enablement_disclosure',
    name: 'Analyze Enablement Disclosure',
    description: 'Evaluate whether the specification provides sufficient disclosure to enable a person skilled in the art',
    task_type: 'legacy',
    coming_soon: true,
    icon: GraduationCap
  },
  {
    id: 'extract_document_citations',
    name: 'Extract Document Citations',
    description: 'Identify and extract citations to patents, publications, and other technical documents from patent specifications',
    task_type: 'legacy',
    coming_soon: true,
    icon: Quote
  }
];

export const TASK_TO_CATEGORY: Record<string, string> = {
  // Legacy
  'parts_list': TASK_CATEGORIES.LEGACY,
  'spec_map': TASK_CATEGORIES.LEGACY,
  'claims_support': TASK_CATEGORIES.LEGACY,
  'claim_chart': TASK_CATEGORIES.LEGACY,
  'claims_with_numerals': TASK_CATEGORIES.LEGACY,
  'custom_analysis': TASK_CATEGORIES.LEGACY,
  'compare_claims': TASK_CATEGORIES.LEGACY,
  'add_numerals_to_abstract': TASK_CATEGORIES.LEGACY,
  'antecedent_basis_check': TASK_CATEGORIES.LEGACY,
  'compare_prior_art_to_claims': TASK_CATEGORIES.LEGACY,
  'find_specification_support': TASK_CATEGORIES.LEGACY,
  'assess_claim_definiteness': TASK_CATEGORIES.LEGACY,
  'analyze_enablement_disclosure': TASK_CATEGORIES.LEGACY,
  'extract_document_citations': TASK_CATEGORIES.LEGACY,

  // Invention Record
  'ir_metadata': TASK_CATEGORIES.INVENTION_RECORD,
  'ir_innovation': TASK_CATEGORIES.INVENTION_RECORD,
  'ir_prior_art': TASK_CATEGORIES.INVENTION_RECORD,
  'ir_tech_class': TASK_CATEGORIES.INVENTION_RECORD,
  'technical_outline': TASK_CATEGORIES.INVENTION_RECORD,
  'parts_process_outline': TASK_CATEGORIES.INVENTION_RECORD,
  'prior_art_analysis': TASK_CATEGORIES.INVENTION_RECORD,
  'patent_drawing': TASK_CATEGORIES.DRAFT_PROCESSING,

  // Draft Processing
  'draft_detailed_description': TASK_CATEGORIES.DRAFT_PROCESSING,
  'draft_other_embodiments': TASK_CATEGORIES.DRAFT_PROCESSING,
  'draft_claims': TASK_CATEGORIES.DRAFT_PROCESSING,
  'draft_background': TASK_CATEGORIES.DRAFT_PROCESSING,
  'draft_summary': TASK_CATEGORIES.DRAFT_PROCESSING,

  // Form Processing
  'form_ids': TASK_CATEGORIES.FORM_PROCESSING,
  'form_ads': TASK_CATEGORIES.FORM_PROCESSING,
  'form_checklist': TASK_CATEGORIES.FORM_PROCESSING,
  'form_ids_check': TASK_CATEGORIES.FORM_PROCESSING,

  // Office Action
  'oa_extract': TASK_CATEGORIES.OFFICE_ACTION,
  'oa_analyze': TASK_CATEGORIES.OFFICE_ACTION,
  'oa_summary_art': TASK_CATEGORIES.OFFICE_ACTION,
  'oa_timeline': TASK_CATEGORIES.OFFICE_ACTION,
  'oa_file_wrapper_notes': TASK_CATEGORIES.OFFICE_ACTION,
  'oa_reporting_email': TASK_CATEGORIES.OFFICE_ACTION,

  // Translation
  'translate_document': TASK_CATEGORIES.TRANSLATION,
  'translate_claims': TASK_CATEGORIES.TRANSLATION,
  'translate_abstract': TASK_CATEGORIES.TRANSLATION,
  'latex_to_docx': TASK_CATEGORIES.TRANSLATION,
  'ocr_pdf': TASK_CATEGORIES.TRANSLATION,

  // Multi-Doc Analysis
  'multi_doc_analysis': TASK_CATEGORIES.LEGACY,
};
